import {Grid, Switch, TextField, Typography, withStyles} from '@material-ui/core';
import Box, {BoxProps} from '@material-ui/core/Box';
import {createStyles, makeStyles} from '@material-ui/core/styles';
import {addHours, addMinutes, format} from 'date-fns';
import {observer} from 'mobx-react-lite';
import {CSSProperties, FC, ReactNode, useEffect, useMemo, useState, useRef, useLayoutEffect} from 'react';
import NumberFormat from 'react-number-format';
import {FormType, ValueChanged} from '../Agent/PreQualification/types';
import {TimezoneNumber} from './helpers/TimezoneNumber';
import {Parameter, ParameterStore} from './mobx/ParameterStore/ParameterStore';

const useStyles = makeStyles(() =>
    createStyles({
        icon: {
            marginRight: 4,
        },
        approved: {
            color: 'green',
        },
        denied: {
            color: 'red',
        },
        sectionTitle: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 46,
            width: 46,
            borderRadius: '50%',
            backgroundColor: '#e8edec',
            color: '#456d69',
            marginRight: 21,
            fontSize: 26,
            textAlign: 'center',
        },
        sectionTitleWrapper: {
            display: 'flex',
            alignItems: 'center',
            textTransform: 'capitalize',
        },
    }),
);

interface TabPanelProps extends BoxProps {
    children?: ReactNode;
    index: any;
    value: any;
    style?: CSSProperties;
    flatten?: boolean;
}

interface CategoryHeaderProps {
    name: string;
    number?: number;
    error?: boolean;
}

export function CategoryHeader(props: CategoryHeaderProps) {
    const classes = useStyles();
    const {name, number, error} = props;
    return (
        <div className={classes.sectionTitleWrapper}>
            {number && <span className={classes.sectionTitle}>{number}</span>}
            {error ? (
                <Typography color="error" style={{fontWeight: 'bold', fontSize: 24}}>
                    {name}
                </Typography>
            ) : (
                <Typography style={{fontWeight: 'bold', fontSize: 24}}>{name}</Typography>
            )}
        </div>
    );
}
export function TabPanel(props: TabPanelProps) {
    const {children, value, index, flatten, style: styleProp = {}, ...other} = props;

    const style = {
        width: '100%',
        scrollTop: -100,
        ...styleProp,
    };

    if (value !== index) return null;

    const content = useMemo(
        () =>
            flatten ? (
                children
            ) : (
                <Box p={1} height="calc(100% - 16px)" width="100%">
                    {children}
                </Box>
            ),
        [children, flatten],
    );

    return (
        <Box
            id={`tab${index}`}
            role="tabpanel"
            hidden={value !== index}
            aria-labelledby={`simple-tab-${index}`}
            style={style}
            {...other}
        >
            {content}
        </Box>
    );
}

TabPanel.defaultProps = {style: {}};

export function NumberFormatCustom(props: any) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator
            isNumericString
            prefix="$"
        />
    );
}

interface PercentageFormatCustomProps {
    inputRef: (instance: NumberFormat | null) => void;
    onChange: (event: {target: {name: string; value: string}}) => void;
    name: string;
}

export function PercentageFormatPreciseCustom(props: PercentageFormatCustomProps) {
    const {inputRef, onChange, name, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator
            isNumericString
            suffix="%"
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
        />
    );
}

export function PercentageFormatCustom(props: PercentageFormatCustomProps) {
    const {inputRef, onChange, name, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator
            isNumericString
            suffix="%"
            decimalScale={0}
            fixedDecimalScale
            allowNegative={false}
        />
    );
}
export function CurrencyFormatCustom(props: any) {
    const {inputRef, onChange, name, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator
            isNumericString
            prefix="$"
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
        />
    );
}

export function DecimalNumberFormatCustom(props: any) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator
            isNumericString
            prefix="$"
        />
    );
}

export function PhoneFormatCustom(props: any) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            isNumericString
            format={'(###) ###-####'}
            mask={'_'}
        />
    );
}

export function SsnFormatCustom(props: any) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            isNumericString
            format={'#########'}
            mask={'_'}
        />
    );
}

export function Spacing({height}: {height: number}) {
    return <div style={{height: height}}></div>;
}

interface TextboxProps {
    error?: any;
    helperText?: any;
    id?: any;
    label?: any;
    value?: any;
    multiline?: any;
    rows?: any;
    onChange?: (text: string) => void;
    style?: any;
    InputProps?: any;
    InputLabelProps?: any;
    type?: any;
    placeholder?: string;
    mask?: string;
    maskChar?: string;
    fullWidth?: boolean;
    variant?: 'filled' | 'outlined' | 'standard';
    shrink?: boolean;
    disabled?: boolean;
}

const useStylesTextBox = makeStyles({
    focused: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        whiteSpace: 'normal !important',
        width: 'auto !important',
        color: 'blue',
    },
    root: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '100%',
    },
});

export const TextBox = (props: TextboxProps) => {
    const {value, onChange} = props;
    const [fieldValue, setFieldValue] = useState(value);
    const classes = useStylesTextBox();
    const onWheelChange = (e: any) => {
        if (props.type == 'number') {
            e.target.blur();
        }
    };
    useEffect(() => {
        setFieldValue(removeEmojis(value));
    }, [value]);

    const removeEmojis = (text: string | null | undefined) => {
        return typeof text === 'string'
            ? text.replace(
                  /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
                  '',
              )
            : text;
    };

    const {
        style,
        fullWidth,
        shrink,
        disabled,
        placeholder,
        error,
        helperText,
        id,
        label,
        multiline,
        rows,
        InputProps,
        type,
    } = props;

    const filed = (
        <TextField
            style={style || {width: '100%', marginTop: 8}}
            fullWidth={fullWidth}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            id={id}
            label={label}
            value={fieldValue}
            disabled={disabled}
            multiline={multiline}
            rows={rows}
            onChange={(e) => {
                setFieldValue(removeEmojis(e.target.value));
            }}
            onBlur={(e) => {
                if (onChange && fieldValue !== value) {
                    onChange(fieldValue);
                }
            }}
            InputProps={InputProps}
            InputLabelProps={{
                classes: {
                    root: classes.root,
                    focused: classes.focused,
                },
                shrink,
            }}
            type={type}
            autoComplete="new-password"
            variant="outlined"
            onWheel={onWheelChange}
        />
    );

    return filed;
};

interface DateTimeInputProps {
    p: ExtendedParameter;
    parameterStore: ParameterStore;
    valueChanged: (p: Parameter, value: any, formType?: 'single-quote' | 'combination-quote') => void;
    formType?: 'single-quote' | 'combination-quote';
}

//@ts-ignore
Date.prototype.stdTimezoneOffset = function () {
    var jan = new Date(this.getFullYear(), 0, 1);
    var jul = new Date(this.getFullYear(), 6, 1);
    return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
};

//@ts-ignore
Date.prototype.isDstObserved = function () {
    //@ts-ignore
    return this.getTimezoneOffset() < this.stdTimezoneOffset();
};

export const DateTimeInput: FC<DateTimeInputProps> = observer(({p, parameterStore, valueChanged, formType}) => {
    const phone = parameterStore.approval.phoneNumber.value;
    const info = TimezoneNumber.getLocalInfo(phone) as any;
    const location = info?.location;
    const timeZone = info?.time?.zone;

    let offset = addMinutes(new Date(p.value), new Date(p.value).getTimezoneOffset());

    // if dual time zone, pick the first time zone
    if (Array.isArray(info?.offset)) {
        info.offset = info.offset[0];
    }

    //@ts-ignore
    if (offset.isDstObserved()) {
        // +1 for summer time
        offset = addHours(offset, info?.offset + 1);
    } else {
        offset = addHours(offset, info?.offset);
    }
    const currDateZone = p.value ? offset : void 0;

    return (
        <div style={{position: 'relative'}}>
            <div style={{position: 'absolute', left: -13, top: 7}}>
                {p.required === true ? <span style={{color: 'red', fontSize: '26px'}}>*</span> : null}
            </div>
            <TextBox
                label={p.label}
                value={p.value.length ? format(new Date(p.value), "yyyy-MM-dd'T'HH:mm") : 0}
                style={{width: '100%', marginTop: 20}}
                onChange={(e: any) => valueChanged(p, e, formType)}
                type="datetime-local"
                InputLabelProps={{
                    shrink: true,
                }}
            />
            {location && timeZone && (
                <span style={{marginTop: 5, fontSize: 12}}>
                    {location} {timeZone},{' '}
                    {!!currDateZone && format(new Date(currDateZone), "yyyy-MM-dd, hh:mm aaaaa'm'")}
                </span>
            )}
        </div>
    );
});

type ExtendedParameter = Parameter & {
    label: string;
    name: string;
    category?: string;
    required?: boolean;
};

const PrequalSwitch = withStyles({
    switchBase: {
        color: 'white',
        '&$checked': {
            color: 'white',
        },
        '&$disabled': {
            color: 'white',
        },
        '&$disabled + $track': {
            backgroundColor: '#F65858',
            opacity: 1,
        },
        '&$checked + $track': {
            backgroundColor: '#456d69',
            opacity: 1,
        },
    },
    checked: {},
    track: {},
    disabled: {},
})(Switch);

interface SwitchCustomProps {
    param: ExtendedParameter;
    required: string | undefined | JSX.Element;
    valueChanged: ValueChanged;
    valueChangedForm?: any;
    form?: boolean;
    formType?: FormType;
    provideDetailParam?: Parameter;
    provideDetailParamTitle?: string;
}

export function SwitchCustom({
    param,
    required,
    valueChangedForm,
    form,
    valueChanged,
    formType,
    provideDetailParam,
    provideDetailParamTitle,
}: SwitchCustomProps) {
    const bvalue = param.value === true || param.value === 'true';
    return (
        <div id={`${param.name}-label`} style={{width: '100%'}}>
            <div>{param.label}</div>
            <Grid container spacing={1}>
                <Grid item xs={provideDetailParam && bvalue ? 1 : 12}>
                    <div
                        id={`${param.name}-hitbox`}
                        style={{
                            cursor: 'pointer',
                            width: 'fit-content',
                            marginTop: provideDetailParam && bvalue ? 18 : 4,
                        }}
                        role="switch"
                        tabIndex={0}
                        aria-checked={bvalue}
                        onClick={() => {
                            if (form) {
                                valueChangedForm(!bvalue);
                            }
                            valueChanged(param, !bvalue, formType);
                        }}
                        onKeyDown={() => {
                            if (form) {
                                valueChangedForm(!bvalue);
                            }
                            valueChanged(param, !bvalue, formType);
                        }}
                    >
                        <PrequalSwitch
                            id={param.name}
                            checked={bvalue}
                            disabled={param.value === '' || param.value === 'null' || param.value == null}
                        />
                        {bvalue && 'Yes'} {param.value === false && 'No'}{' '}
                        {(param.value === '' || param.value === 'null' || param.value == null) && 'Unanswered'}{' '}
                        {required}
                    </div>
                </Grid>
                {provideDetailParam && bvalue && (
                    <Grid item xs>
                        <TextBox
                            id={'incomeIndividual'}
                            label={provideDetailParamTitle || 'Please Provide More Detail'}
                            value={provideDetailParam?.value || ''}
                            onChange={(value) => valueChanged(provideDetailParam, value)}
                        />
                    </Grid>
                )}
            </Grid>
        </div>
    );
}

interface SwitchCustomBasicProps {
    value: boolean;
    name: string;
    label: string;
    required: string | undefined | JSX.Element;
    onChange: (value: boolean) => void;
}

export function SwitchCustomBasic({value, name, label, required, onChange}: SwitchCustomBasicProps) {
    return (
        <div id={`${name}-label`} style={{width: '100%'}}>
            <div>{label}</div>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <div
                        id={`${name}-hitbox`}
                        style={{
                            cursor: 'pointer',
                            width: 'fit-content',
                            marginTop: 4,
                        }}
                        role="switch"
                        tabIndex={0}
                        aria-checked={value}
                        onClick={() => {
                            onChange(!value);
                        }}
                        onKeyDown={() => {
                            onChange(!value);
                        }}
                    >
                        <PrequalSwitch id={name} checked={value} />
                        {value && 'Yes'} {value === false && 'No'}
                        {required}
                    </div>
                </Grid>
            </Grid>
        </div>
    );
}

interface SegmentedYesNoSwitchProps {
    checked?: boolean;
    disabled?: boolean;
}

const SegmentedYesNoSwitch = ({checked, disabled}: SegmentedYesNoSwitchProps) => {
    let yesColor = '#757575';
    let yesWeight = 400;
    let yesBg = 'transparent';
    let noColor = '#757575';
    let noWeight = 400;
    let noBg = 'transparent';
    const dividerColor = '#E0E0E0';
    let rootBorder = '1px solid #E0E0E0';
    let rootBg = '#FFF';

    if (disabled) {
        yesColor = noColor = '#757575';
        yesWeight = noWeight = 400;
        rootBorder = '1.5px solid #E0E0E0';
        rootBg = '#FFF';
    } else if (checked === true) {
        yesColor = '#456d69';
        yesWeight = 700;
        yesBg = 'rgba(63,81,181,0.12)';
        rootBorder = '1.5px solid #456d69';
        noColor = '#757575';
        noWeight = 400;
        noBg = 'transparent';
    } else if (checked === false) {
        noColor = '#456d69';
        noWeight = 700;
        noBg = 'rgba(63,81,181,0.12)';
        rootBorder = '1.5px solid #456d69';
        yesColor = '#757575';
        yesWeight = 400;
        yesBg = 'transparent';
    } else {
        yesColor = noColor = '#757575';
        yesWeight = noWeight = 400;
        yesBg = noBg = 'transparent';
        rootBorder = '1.5px solid #E0E0E0';
        rootBg = '#FFF';
    }
    const yesNoBaseStyle: React.CSSProperties = {
        fontFamily: 'Fredoka',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 16,
        lineHeight: '160%',
        letterSpacing: 0,
        verticalAlign: 'middle',
        textTransform: 'capitalize',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        height: '100%',
    };
    return (
        <div
            style={{
                width: 233,
                height: 48,
                display: 'flex',
                borderRadius: 8,
                border: rootBorder,
                background: rootBg,
                overflow: 'hidden',
                userSelect: 'none',
            }}
        >
            <div
                style={{
                    ...yesNoBaseStyle,
                    color: yesColor,
                    fontWeight: yesWeight,
                    background: yesBg,
                }}
            >
                Yes
            </div>
            <div
                style={{
                    width: 1,
                    height: '100%',
                    background: dividerColor,
                    alignSelf: 'stretch',
                }}
            />
            <div
                style={{
                    ...yesNoBaseStyle,
                    color: noColor,
                    fontWeight: noWeight,
                    background: noBg,
                }}
            >
                No
            </div>
        </div>
    );
};

export const labelTextStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'Fredoka',
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: 18,
    lineHeight: '160%',
    letterSpacing: 0,
    textTransform: 'capitalize',
    color: '#838383',
    verticalAlign: 'middle',
};

interface SwitchSegmentedYesNoBasicProps {
    value: boolean | '' | undefined;
    name: string;
    label?: string;
    required?: string | JSX.Element;
    onChange: (value: boolean) => void;
    style?: React.CSSProperties;
    horizontal?: boolean;
}

export function SwitchSegmentedYesNoBasic({
    value,
    name,
    label,
    required,
    onChange,
    style,
    horizontal,
}: SwitchSegmentedYesNoBasicProps) {
    const labelId = `${name}-title`;
    // Treat empty string as undefined for display
    const displayValue = value === '' ? undefined : value;

    if (horizontal) {
        return (
            <div
                id={`${name}-label`}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 24,
                    marginBottom: 24,
                }}
            >
                {label && (
                    <div
                        id={labelId}
                        style={{
                            ...labelTextStyle,
                            minWidth: 320,
                            maxWidth: 600,
                            flexShrink: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal', // allow wrapping
                        }}
                    >
                        {required ? <span>{required}</span> : null}
                        <span>{label}</span>
                    </div>
                )}
                <div
                    id={`${name}-hitbox`}
                    style={{
                        cursor: 'pointer',
                        width: 'fit-content',
                        marginTop: 0,
                        display: 'flex',
                        alignItems: 'center',
                        ...style,
                    }}
                    role="switch"
                    tabIndex={0}
                    aria-checked={displayValue === true}
                    aria-labelledby={labelId}
                    onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        if (x < rect.width / 2) {
                            // Clicked on "Yes"
                            onChange(true);
                        } else {
                            // Clicked on "No"
                            onChange(false);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            onChange(displayValue !== true);
                        }
                    }}
                >
                    <SegmentedYesNoSwitch checked={displayValue} />
                </div>
            </div>
        );
    }

    const labelRef = useRef<HTMLDivElement>(null);
    const [isMultiline, setIsMultiline] = useState(false);

    useLayoutEffect(() => {
        if (labelRef.current) {
            const lineHeight = 28.8;
            setIsMultiline(labelRef.current.offsetHeight > lineHeight + 2);
        }
    }, [label, required]);

    return (
        <div id={`${name}-label`} style={{width: '100%'}}>
            {label && (
                <div id={labelId} ref={labelRef} style={labelTextStyle}>
                    {required ? <span>{required}</span> : null}
                    <span>{label}</span>
                </div>
            )}

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <div
                        id={`${name}-hitbox`}
                        style={{
                            cursor: 'pointer',
                            width: 'fit-content',
                            marginTop: isMultiline ? 4 : 12,
                            display: 'flex',
                            alignItems: 'center',
                            ...style,
                        }}
                        role="switch"
                        tabIndex={0}
                        aria-checked={displayValue === true}
                        aria-labelledby={labelId}
                        onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            if (x < rect.width / 2) {
                                // Clicked on "Yes"
                                onChange(true);
                            } else {
                                // Clicked on "No"
                                onChange(false);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') onChange(displayValue !== true);
                        }}
                    >
                        <SegmentedYesNoSwitch checked={displayValue} />
                    </div>
                </Grid>
            </Grid>
        </div>
    );
}

interface SwitchSegmentedYesNoProps {
    param: ExtendedParameter;
    required?: string | JSX.Element;
    valueChanged: ValueChanged;
    formType?: FormType;
    horizontal?: boolean;
}

export function SwitchSegmentedYesNo({param, required, valueChanged, formType, horizontal}: SwitchSegmentedYesNoProps) {
    let checked: boolean | undefined = undefined;
    if (param.value === true || param.value === 'true') checked = true;
    else if (param.value === false || param.value === 'false') checked = false;
    const labelId = `${param.name}-title`;

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x < rect.width / 2) {
            valueChanged(param, true, formType);
        } else {
            valueChanged(param, false, formType);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            valueChanged(param, checked !== true, formType);
        }
    };

    if (horizontal) {
        return (
            <div
                id={`${param.name}-label`}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 24,
                    marginBottom: 24,
                }}
            >
                <div
                    id={labelId}
                    style={{
                        ...labelTextStyle,
                        minWidth: 320,
                        maxWidth: 600,
                        flexShrink: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {required ? <span>{required}</span> : null}
                    <span>{param.label}</span>
                </div>
                <div
                    id={`${param.name}-hitbox-segmented`}
                    style={{
                        cursor: 'pointer',
                        width: 'fit-content',
                        marginTop: 0,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                    role="switch"
                    aria-checked={checked}
                    aria-labelledby={labelId}
                    tabIndex={0}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                >
                    <SegmentedYesNoSwitch checked={checked} />
                </div>
            </div>
        );
    }

    const labelRef = useRef<HTMLDivElement>(null);
    const [isMultiline, setIsMultiline] = useState(false);

    useLayoutEffect(() => {
        if (labelRef.current) {
            const lineHeight = 28.8;
            setIsMultiline(labelRef.current.offsetHeight > lineHeight + 2);
        }
    }, [param.label, required]);

    return (
        <div id={`${param.name}-label`} style={{width: '100%'}}>
            <div id={labelId} ref={labelRef} style={labelTextStyle}>
                {required ? <span>{required}</span> : null}
                <span>{param.label}</span>
            </div>

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <div
                        id={`${param.name}-hitbox-segmented`}
                        style={{
                            cursor: 'pointer',
                            width: 'fit-content',
                            marginTop: isMultiline ? 4 : 12,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        role="switch"
                        aria-checked={checked}
                        aria-labelledby={labelId}
                        tabIndex={0}
                        onClick={handleClick}
                        onKeyDown={handleKeyDown}
                    >
                        <SegmentedYesNoSwitch checked={checked} />
                    </div>
                </Grid>
            </Grid>
        </div>
    );
}
